import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { api } from '../services/api';

/**
 * UnpaidBanner (Payment Tracking v2)
 *
 * Site-wide banner shown while the user has unpaid entries. Links to the
 * Settle Up page. Best-effort: any error just hides the banner.
 */

const fmtMoney = (n: number) => (n % 1 === 0 ? String(n) : n.toFixed(2));

export function UnpaidBanner() {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const location = useLocation();
  const [totalOwed, setTotalOwed] = useState(0);
  const [entryCount, setEntryCount] = useState(0);

  useEffect(() => {
    let alive = true;
    async function load() {
      if (!isAuthenticated) {
        setTotalOwed(0);
        return;
      }
      try {
        const token = await getAccessTokenSilently();
        const balance = await api.getMyBalance(token);
        if (alive) {
          setTotalOwed(balance.totalOwed);
          setEntryCount(balance.entryCount);
        }
      } catch {
        // Banner is best-effort — never block the app on it
      }
    }
    load();
    return () => {
      alive = false;
    };
  }, [isAuthenticated, getAccessTokenSilently, location.pathname]);

  if (!isAuthenticated || totalOwed <= 0) return null;
  if (location.pathname === '/settle-up') return null;

  return (
    <div className="bg-amber-100 border-b border-amber-300">
      <div className="px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between gap-3 text-sm max-w-7xl mx-auto">
        <span className="text-amber-900 font-medium">
          💵 You owe ${fmtMoney(totalOwed)} across {entryCount}{' '}
          {entryCount === 1 ? 'entry' : 'entries'}
        </span>
        <Link
          to="/settle-up"
          className="shrink-0 bg-amber-500 hover:bg-amber-600 text-white font-semibold px-3 py-1 rounded-lg transition-colors"
        >
          Settle up →
        </Link>
      </div>
    </div>
  );
}
